"use client";
import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "next/navigation";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getPopularTramites } from "@/utils/popularTramite";
import TarjetaEstadoCuenta from "./components/TarjetaEstadoCuenta";
import { openMenuLink } from "@/utils/openMenuLink";
import Toast from "@/components/Toast/Toast";
import SecurityTipBanner from "@/components/SecurityAwareness/SecurityTipBanner";
import { useDeudas } from "@/hooks/useDeudas";
import { HiOutlineArrowTopRightOnSquare } from "react-icons/hi2";

export default function Inicio() {
  const services = getPopularTramites();
  const { user } = useAuth();
  const router = useRouter();
  const { total, isLoading, error, statusInfo } = useDeudas();

  const settings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    responsive: [
      {
        breakpoint: 1024, // Tablets y pantallas medianas
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 768, // Pantallas pequeñas
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: true,
          centerPadding: "10px", // Evita que los slides se colapsen
        },
      },
    ],
  };

  return (
    <div className="font-sans mx-auto space-y-16">
      {/* Estado de cuenta y servicio municipales */}
      <div className="space-y-4">
        <SecurityTipBanner />
        <Toast
          type="info"
          visible={statusInfo}
          variant="soft"
          className="text-primary"
        >
          <div className="flex items-center justify-between">
            <p className="text-md">
              El contribuyente <strong>{statusInfo?.message} </strong> no ha
              presentado su
              <strong> declaración de patente municipal</strong> en el plazo
              establecido.
            </p>
            <button
              type="button"
              onClick={() => router.push("/patente/declaracion")}
              className="flex w-fit items-center justify-center gap-4 rounded-xl bg-yellow-700 px-4 py-2 text-sm font-medium text-white transition hover:scale-[1.02]"
            >
              <HiOutlineArrowTopRightOnSquare />
              Realizar declaración
            </button>
          </div>
        </Toast>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Amount Due Card */}
          {user && (
            <TarjetaEstadoCuenta
              user={user}
              deudas={{ total, isLoading, error, statusInfo }}
            />
          )}

          {/* Main Portal Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className=" relative overflow-hidden rounded-2xl bg-primary  shadow-2xl"
          >
            {/* Fondo decorativo */}

            <div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-white/10" />

            <div className="flex flex-col items-center justify-between p-8 md:flex-row">
              <div className="relative z-10 text-center md:text-left">
                <motion.h1
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-3 text-3xl font-bold tracking-tight drop-shadow-md md:text-4xl"
                >
                  <span className="bg-clip-text text-slate-400">
                    Servicios Municipales
                  </span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-lg font-medium text-blue-50/95 md:text-xl"
                >
                  GAD Municipal de Portoviejo
                </motion.p>

                {/* Botones de acción */}
                <motion.div
                  className="mt-6 flex justify-center gap-3 md:justify-start"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <button
                    className="rounded-lg bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20"
                    onClick={() =>
                      openMenuLink({
                        to: "https://online.portoviejo.gob.ec/Servicios/SeparacionTurnos.aspx",
                        router,
                        newTab: true,
                        onError: () => {
                          // toast/dialog
                        },
                      })
                    }
                  >
                    Obtener turno en línea
                  </button>
                </motion.div>
              </div>

              {/* Imagen con efecto flotante */}
              <motion.img
                src="../images/illustration/illustration-02.png"
                alt="Portal illustration"
                width={220}
                height={160}
                className="hidden md:block"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
              />
            </div>

            {/* Indicador de progreso mejorado */}
            <div className="px-8 pb-4">
              <div className="flex items-center justify-center gap-2 md:justify-start">
                {[1, 2, 3, 4].map((dot) => (
                  <motion.div
                    key={dot}
                    className="h-1.5 w-6 rounded-full bg-white/30"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      delay: dot * 0.2,
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Servicios mas utilizados */}
      <div className="grid gap-6 md:grid-cols-3">
        <div className="space-y-6 md:col-span-3">
          {/* Service Cards */}
          <div className="text-center md:text-left">
            <h1 className="mb-2 text-2xl font-bold md:text-3xl">
              Servicios más utilizados
            </h1>
          </div>
          <div className="w-full">
            {/* Carrusel para pantallas grandes */}
            <div className="hidden md:block">
              <Slider {...settings} className="w-full">
                {services.map((service, index) => (
                  <div key={index} className="w-full px-2">
                    <motion.div
                      initial={{ opacity: 0.8, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="
          /* 👉 altura 
          igual para
          todas 
          */ group mx-auto flex h-[250px]
          w-full max-w-[300px] flex-col rounded-2xl border

          bg-white           p-4 shadow-sm transition-all duration-300 hover:border-blue-50 hover:shadow-lg sm:p-6
        "
                    >
                      <div className="mb-4 inline-flex justify-center">
                        <div className="rounded-full bg-blue-100/80 p-3 ring-8 ring-blue-50/50 transition-all group-hover:ring-blue-100/30">
                          <service.card.icon className="h-8 w-8 text-blue-600 group-hover:text-blue-700" />
                        </div>
                      </div>

                      <h3 className="mb-2 text-center text-lg font-semibold text-gray-800">
                        {service.name}
                      </h3>

                      <p className="mb-5 line-clamp-3 text-center text-sm leading-relaxed text-gray-600">
                        {service.card.text}
                      </p>

                      {/* 👉 empuja el botón siempre al fondo, igualando altura visual */}
                      <div className="mt-auto">
                        <button
                          className="inline-flex items-center justify-center gap-1 font-medium text-blue-600 transition-colors hover:text-blue-800"
                          onClick={() =>
                            openMenuLink({
                              to: service.path,
                              router,
                              newTab: true, // normalmente los trámites externos quieres que abran en nueva pestaña
                              onError: () => {
                                // aquí pon tu toast/dialog si tienes
                                // toast.error("No se pudo abrir el enlace.");
                              },
                            })
                          }
                        >
                          <span>{service.card.textButton}</span>
                          <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </Slider>
            </div>

            {/* Grid para móviles */}
            <div className="block md:hidden">
              <div className="grid grid-cols-1 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0.8, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="group mx-auto flex w-full max-w-[300px] flex-col rounded-2xl border bg-white p-4 shadow-sm transition-all duration-300 hover:border-blue-50 hover:shadow-lg sm:p-6"
                  >
                    <div className="mb-4 inline-flex justify-center">
                      <div className="rounded-full bg-blue-100/80 p-3 ring-8 ring-blue-50/50 transition-all group-hover:ring-blue-100/30">
                        <service.card.icon className="h-8 w-8 text-blue-600 group-hover:text-blue-700" />
                      </div>
                    </div>

                    <h3 className="mb-2 text-lg font-semibold text-gray-800">
                      {service.name}
                    </h3>
                    <p className="mb-5 text-sm leading-relaxed text-gray-600">
                      {service.card.text}
                    </p>

                    <div className="mt-auto">
                      <button
                        className="inline-flex items-center justify-center gap-1 font-medium text-blue-600 transition-colors hover:text-blue-800"
                        onClick={() => {
                          if (service.path.startsWith("http")) {
                            window.location.href = service.path;
                          } else {
                            router.push(service.path);
                          }
                        }}
                      >
                        <span>{service.card.textButton}</span>
                        <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Payment Banner */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="to-primary-dark group relative flex max-w-sm cursor-pointer flex-col items-center justify-between overflow-hidden rounded-2xl bg-primary p-4 shadow-xl transition-all duration-300 hover:shadow-2xl sm:max-w-full sm:flex-row sm:p-6"
      >
        {/* Fondo decorativo */}
        <div className="absolute right-0 top-0 h-20 w-20 -translate-y-1/3 translate-x-1/3 rounded-full bg-white/10 sm:h-32 sm:w-32" />

        <div className="relative z-10 flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
          {/* Texto principal con animación escalonada */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center sm:text-left"
          >
            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
              <span className=" bg-clip-text text-slate-400">¡No olvides!</span>
              <span className="mt-1 block text-sm font-medium text-white/95 sm:text-lg">
                Pagar tus impuestos prediales
              </span>
            </h2>

            {/* Detalle adicional */}
            <p className="mt-2 flex items-center justify-center gap-2 text-xs text-white/80 sm:justify-start sm:text-sm">
              <Clock className="h-4 w-4" />
              <span>
                Obten un descuento hasta el 30 de junio del año en curso.
              </span>
            </p>
          </motion.div>

          {/* Imagen con efecto flotante (solo en sm+) */}
          <motion.img
            src="../images/illustration/illustration-03.png"
            alt="People illustration"
            width={100}
            height={80}
            className="hidden sm:block sm:w-24 md:w-28"
            initial={{ y: 0 }}
            animate={{ y: [-3, 3, -3] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        {/* Icono con animación (oculto en móviles) */}
        <motion.div
          className="hidden sm:flex"
          initial={{ x: 0 }}
          whileHover={{ x: 5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <ChevronRight className="h-8 w-8 text-white/90" strokeWidth={2.5} />
        </motion.div>

        {/* Banda decorativa inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-green-400/80 to-transparent" />

        {/* Legal disclaimer */}
        <p className="absolute bottom-2 right-4 text-[10px] text-white/50 sm:text-xs">
          *Aplican términos y condiciones
        </p>
      </motion.div>
    </div>
  );
}
